// ══════════════════════════════════════════════════════════════════════════════════════════════
//  B-2254 · UNA ZONA QUE SE AMPLÍA CON DOS DEDOS: EL ZOOM QUE LLEVA CADA VISOR DE DOCUMENTOS     (27-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño: «en iPhone, cuando se abre un PDF o documento del estilo, no se puede pellizcar la pantalla para aumentar o
//  disminuir». Por qué (contado entero en utils/pellizco.js): en la app del iPhone el pellizco de la página está apagado
//  dos veces —Capacitor sin `zoomEnabled` y `maximum-scale=1.0` de B-838— y los visores propios no traían zoom.
//
//  Aquí está el zoom que lleva cada visor, para que no dependa del de la página:
//   · dos dedos acercan y alejan HACIA DONDE ESTÁN (el punto del documento entre los dedos se queda debajo de ellos);
//   · un dedo se mueve por lo ampliado con el desplazamiento nativo de siempre (inercia incluida);
//   · doble toque (o doble clic): de ajustado a ampliado y vuelta;
//   · en el ordenador, la rueda con Ctrl (y el pellizco del trackpad, que llega así) y los botones − / + (ControlesZoom).
//
//  EL ÚNICO `touch-action` DE UN VISOR ESTÁ AQUÍ, y es `pan-x pan-y`: el navegador se queda con el dedo que desplaza, y
//  el pellizco y el doble toque los hace esta zona. Si se los quedara el navegador, en la app no harían nada (lo tiene
//  apagado) y en Safari ampliarían la página entera, con la barra del visor incluida. Nadie más pone `touch-action`
//  ni anula un gesto mientras un visor está abierto: lo vigila backend/routes/zoom-documentos-iphone.test.js.
//
//  ES UN GANCHO, NO UN COMPONENTE: el visor pinta sus tres capas con lo que devuelve (propsExterior > propsZona >
//  propsMarco > propsContenido) y sigue siendo el dueño de lo de dentro (las páginas del PDF que pinta pdf.js, la foto).
//  El zoom cambia el ANCHO del contenido (no una transformación): así el desplazamiento llega a todo lo ampliado, y el
//  PDF se puede volver a pintar con más píxeles al soltar los dedos (`alAfinar`), para que la letra no salga borrosa.
//
//  Uso:
//    const z = useZonaAmpliable({ anchoMax: 980 })                     // un PDF: a lo ancho, sin pasar de 980 px
//    const z = useZonaAmpliable({ proporcion: alto / ancho })          // una foto: cabe entera
//    <div {...z.propsExterior}>
//      <div {...z.propsZona}><div {...z.propsMarco}><div {...z.propsContenido}>…</div></div></div>
//      <ControlesZoom zona={z} />
//    </div>
// ══════════════════════════════════════════════════════════════════════════════════════════════
import { useEffect, useRef, useState } from 'react'
import { ZOOM_MIN, ZOOM_MAX, PASO_BOTON, TOQUE_QUIETO_PX, limitarZoom, iniciarPellizco, moverPellizco, puntoDelContenido, correccion, zoomDeRueda, zoomDeDobleToque, esDobleToque, anchoBase } from './pellizco'
import { t as i18nT } from './i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// Lo que se espera, con los dedos ya quietos, antes de volver a pintar el PDF con más píxeles.
export const ESPERA_AFINAR_MS = 220
// Un doble clic que llega justo después de un toque es el que el navegador fabrica del doble toque: ya está hecho.
const CLIC_DE_UN_TOQUE_MS = 1000

const ahora = () => Date.now()
const rectDe = (el) => { try { return el && el.getBoundingClientRect ? el.getBoundingClientRect() : null } catch (_) { return null } }

// Los gestos, sin React: reciben cómo llegar a la zona y al contenido, y el estado vivo `v` (zoom, ancho base, tope…).
// Se exporta para que la guardia los dispare con eventos de mentira y mida lo que hacen.
export function crearGestos({ v, zona, contenido, setZoom, setMedida }) {
  const g = {}

  function programarAfinado() {
    if (v.temporizador) clearTimeout(v.temporizador)
    v.temporizador = setTimeout(() => {
      v.temporizador = null
      if (typeof v.alAfinar === 'function') { try { v.alAfinar(v.zoom, zona(), v.base) } catch (_) { /* afinar es una mejora: si falla, se ve igual */ } }
    }, ESPERA_AFINAR_MS)
  }

  // Pone el zoom `zNuevo` dejando quieto, debajo del punto de pantalla `p`, el punto del documento `puntoFijo` (o el que
  // estuviera debajo de `p`). Escribe el ancho EN EL MOMENTO (el pellizco no puede esperar a React) y avisa a React.
  g.aplicar = (zNuevo, p, puntoFijo) => {
    const z = limitarZoom(zNuevo, v.max)
    const antes = v.zoom
    const el = zona(), cont = contenido()
    let punto = puntoFijo || null
    if (!punto && p) { const r = rectDe(cont); if (r) punto = puntoDelContenido(r, p, antes) }
    v.zoom = z
    if (cont && cont.style && v.medido && v.base > 0) cont.style.width = Math.round(v.base * z) + 'px'
    if (el && punto && p) {
      const r = rectDe(cont)
      if (r) {
        const { dx, dy } = correccion(r, punto, z, p)
        if (dx) el.scrollLeft = (Number(el.scrollLeft) || 0) + dx
        if (dy) el.scrollTop = (Number(el.scrollTop) || 0) + dy
      }
    }
    setZoom(z)
    if (Math.abs(z - antes) > 1e-3) programarAfinado()
    return z
  }

  // El centro de la zona: hacia ahí acercan los botones.
  const centroZona = () => {
    const r = rectDe(zona())
    return r ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : null
  }
  g.ampliar = () => g.aplicar(v.zoom * PASO_BOTON, centroZona())
  g.reducir = () => g.aplicar(v.zoom / PASO_BOTON, centroZona())
  g.ajustar = () => g.aplicar(ZOOM_MIN, centroZona())
  // Otro documento en el mismo visor: ajustado y arriba del todo.
  g.reiniciar = () => {
    g.aplicar(ZOOM_MIN, null)
    const el = zona()
    if (el) { el.scrollTop = 0; el.scrollLeft = 0 }
  }

  // El pellizco va al ritmo de la pantalla: un cambio por fotograma, el último.
  function enMarco(fn) {
    const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : null
    if (!raf) { fn(); return }
    v.pendiente = fn
    if (v.marco) return
    v.marco = raf(() => { v.marco = 0; const f = v.pendiente; v.pendiente = null; if (f) f() })
  }

  g.alEmpezar = (e) => {
    const t = e && e.touches
    v.ultimoTactil = ahora()
    if (t && t.length >= 2) {
      v.pellizco = iniciarPellizco([t[0], t[1]], v.zoom, rectDe(contenido()) || { left: 0, top: 0 })
      v.toque = null
      v.ultimoToque = null
      if (e.cancelable && e.preventDefault) e.preventDefault()
      return
    }
    if (t && t.length === 1) v.toque = { x: t[0].clientX, y: t[0].clientY, movido: false }
  }

  g.alMover = (e) => {
    const t = e && e.touches
    if (v.pellizco && t && t.length >= 2) {
      // El pellizco es de la zona: que el navegador no lo use para desplazar ni para ampliar la página entera.
      if (e.cancelable && e.preventDefault) e.preventDefault()
      const { zoom, centro } = moverPellizco(v.pellizco, [t[0], t[1]], v.max)
      const punto = v.pellizco.punto
      enMarco(() => g.aplicar(zoom, centro, punto))
      return
    }
    // Un dedo: desplaza (lo hace el navegador). Solo se apunta que ya no es un toque.
    if (v.toque && t && t.length === 1 && Math.hypot(t[0].clientX - v.toque.x, t[0].clientY - v.toque.y) > TOQUE_QUIETO_PX) v.toque.movido = true
  }

  g.alSoltar = (e) => {
    const t = e && e.touches
    const quedan = t ? t.length : 0
    if (v.pellizco) {
      if (quedan < 2) { v.pellizco = null; v.toque = null; v.ultimoToque = null }
      return
    }
    const ct = e && e.changedTouches && e.changedTouches[0]
    const toque = v.toque
    v.toque = null
    if (!v.dobleToque || !toque || toque.movido || !ct || quedan > 0) return
    const este = { x: ct.clientX, y: ct.clientY, t: ahora() }
    if (esDobleToque(v.ultimoToque, este)) {
      v.ultimoToque = null
      // Sin esto, el segundo toque sería además un clic (y en Safari, el zoom de la página).
      if (e.cancelable && e.preventDefault) e.preventDefault()
      g.aplicar(zoomDeDobleToque(v.zoom, v.max), { x: este.x, y: este.y })
    } else {
      v.ultimoToque = este
    }
  }

  g.alCancelar = () => { v.pellizco = null; v.toque = null }

  g.alRueda = (e) => {
    if (!e || !(e.ctrlKey || e.metaKey)) return   // la rueda sola desplaza, como siempre
    if (e.cancelable && e.preventDefault) e.preventDefault()
    g.aplicar(zoomDeRueda(v.zoom, e.deltaY, v.max), { x: e.clientX, y: e.clientY })
  }

  g.alDobleClic = (e) => {
    if (!v.dobleToque || !e) return
    if (ahora() - (v.ultimoTactil || 0) < CLIC_DE_UN_TOQUE_MS) return
    g.aplicar(zoomDeDobleToque(v.zoom, v.max), { x: e.clientX, y: e.clientY })
  }

  // Engancharse a la zona que haya ahora. Los oyentes táctiles y la rueda van A MANO y NO PASIVOS: React los registra
  // pasivos, y el preventDefault del pellizco no pararía nada.
  g.enganchar = (el) => {
    if (!el || typeof el.addEventListener !== 'function') return
    const opc = { passive: false }
    el.addEventListener('touchstart', g.alEmpezar, opc)
    el.addEventListener('touchmove', g.alMover, opc)
    el.addEventListener('touchend', g.alSoltar, opc)
    el.addEventListener('touchcancel', g.alCancelar)
    el.addEventListener('wheel', g.alRueda, opc)
    const medir = () => {
      const ancho = Number(el.clientWidth) || 0, alto = Number(el.clientHeight) || 0
      setMedida((m) => (m.ancho === ancho && m.alto === alto ? m : { ancho, alto }))
    }
    let ro = null, conVentana = false
    if (typeof ResizeObserver === 'function') { try { ro = new ResizeObserver(medir); ro.observe(el) } catch (_) { ro = null } }
    if (!ro && typeof window !== 'undefined' && window && typeof window.addEventListener === 'function') {
      window.addEventListener('resize', medir); conVentana = true
    }
    medir()
    v.enganchada = el
    v.quitar = () => {
      el.removeEventListener('touchstart', g.alEmpezar, opc)
      el.removeEventListener('touchmove', g.alMover, opc)
      el.removeEventListener('touchend', g.alSoltar, opc)
      el.removeEventListener('touchcancel', g.alCancelar)
      el.removeEventListener('wheel', g.alRueda, opc)
      if (ro) { try { ro.disconnect() } catch (_) { /* nada */ } }
      if (conVentana) window.removeEventListener('resize', medir)
    }
  }

  g.soltar = (final) => {
    if (v.quitar) { try { v.quitar() } catch (_) { /* nada */ } }
    v.quitar = null
    v.enganchada = null
    v.pellizco = null
    if (final) {
      if (v.temporizador) { clearTimeout(v.temporizador); v.temporizador = null }
      if (v.marco && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(v.marco)
      v.marco = 0
      v.pendiente = null
    }
  }

  return g
}

export function useZonaAmpliable({ proporcion = 0, anchoMax = 0, margen = 0, max = ZOOM_MAX, dobleToque = true, centrarAlto = false, alAfinar = null, etiqueta = '' } = {}) {
  const refZona = useRef(null)
  const refContenido = useRef(null)
  const [zoom, setZoom] = useState(ZOOM_MIN)
  const [medida, setMedida] = useState({ ancho: 0, alto: 0 })
  const vivo = useRef(null)
  if (!vivo.current) vivo.current = { zoom: ZOOM_MIN, base: 0, medido: false }
  const v = vivo.current
  const medido = medida.ancho > 0
  const base = anchoBase({ ancho: medida.ancho, alto: medida.alto, proporcion, anchoMax, margen })
  Object.assign(v, { base, medido, max, dobleToque, alAfinar })
  if (!v.gestos) v.gestos = crearGestos({ v, zona: () => refZona.current, contenido: () => refContenido.current, setZoom, setMedida })
  const g = v.gestos

  // La zona puede aparecer después (un visor que se abre al pulsar): se engancha a la que haya en cada pintado.
  useEffect(() => {
    const el = refZona.current
    if (el !== v.enganchada) { g.soltar(false); if (el) g.enganchar(el) }
  })
  useEffect(() => () => g.soltar(true), [])   // eslint-disable-line react-hooks/exhaustive-deps

  const propsExterior = {
    'data-marco-zoom': '',
    style: { position: 'relative', flex: '1 1 auto', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' },
  }
  const propsZona = {
    ref: refZona,
    'data-zona-ampliable': '',
    role: 'region',
    tabIndex: 0,
    'aria-label': etiqueta || i18nT('utils.zonaAmpliable.documento_pellizca_toca_dos_veces', 'Documento: pellizca, toca dos veces o usa los botones para ampliar'),
    onDoubleClick: g.alDobleClic,
    style: {
      position: 'relative', flex: '1 1 auto', minHeight: 0, minWidth: 0, width: '100%', boxSizing: 'border-box',
      overflow: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', overflowAnchor: 'none', outline: 'none',
      touchAction: 'pan-x pan-y',
    },
  }
  const propsMarco = {
    style: {
      minWidth: '100%', minHeight: '100%', width: 'max-content', boxSizing: 'border-box', padding: margen ? margen + 'px' : 0,
      display: 'flex', justifyContent: 'center', alignItems: centrarAlto ? 'center' : 'flex-start',
    },
  }
  const propsContenido = {
    ref: refContenido,
    'data-contenido-ampliable': '',
    style: medido
      ? { width: Math.round(base * zoom) + 'px', flex: '0 0 auto', maxWidth: 'none' }
      : { width: '100%', flex: '0 0 auto', opacity: 0 },
  }
  return {
    zoom, max, medido, anchoBase: base,
    anchoBaseActual: () => v.base,
    zoomActual: () => v.zoom,
    ampliar: g.ampliar, reducir: g.reducir, ajustar: g.ajustar, reiniciar: g.reiniciar, ponerZoom: g.aplicar,
    refZona, refContenido, propsExterior, propsZona, propsMarco, propsContenido,
  }
}

// Los botones − / porcentaje / +. Sin estado propio (lo lleva el gancho): se pinta igual en el visor oscuro y en una
// vista previa clara. El porcentaje devuelve a «ajustado».
export function ControlesZoom({ zona, abajo = 12 }) {
  if (!zona || !zona.medido) return null
  const z = zona.zoom || 1
  const boton = {
    minWidth: 44, height: 44, padding: '0 6px', border: 'none', borderRadius: 999, background: 'transparent', color: '#fff',
    fontSize: 22, fontWeight: 700, lineHeight: 1, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'inherit',
  }
  return (
    // El clic no sube: en el visor de fotos, tocar fuera de la foto la cierra, y un botón del zoom no es «fuera».
    <div data-controles-zoom="" role="group" aria-label={i18nT('utils.zonaAmpliable.zoom_del_documento', 'Zoom del documento')} onClick={(e) => { if (e && e.stopPropagation) e.stopPropagation() }}
      style={{ position: 'absolute', right: 12, bottom: abajo, zIndex: 2, display: 'flex', alignItems: 'center', gap: 2, padding: 3,
        borderRadius: 999, background: 'rgba(15,18,24,0.74)', boxShadow: '0 4px 16px rgba(0,0,0,0.3)', backdropFilter: 'blur(6px)' }}>
      <button type="button" onClick={zona.reducir} disabled={z <= ZOOM_MIN + 1e-3} aria-label={i18nT('utils.zonaAmpliable.reducir', 'Reducir')}
        style={{ ...boton, opacity: z <= ZOOM_MIN + 1e-3 ? 0.4 : 1 }}>−</button>
      <button type="button" onClick={zona.ajustar} aria-label={i18nT('utils.zonaAmpliable.ajustar_a_la_pantalla', 'Ajustar a la pantalla')} title={i18nT('utils.zonaAmpliable.ajustar_a_la_pantalla', 'Ajustar a la pantalla')}
        style={{ ...boton, fontSize: 13, minWidth: 58 }}>{Math.round(z * 100)} %</button>
      <button type="button" onClick={zona.ampliar} disabled={z >= zona.max - 1e-3} aria-label={i18nT('utils.zonaAmpliable.ampliar', 'Ampliar')}
        style={{ ...boton, opacity: z >= zona.max - 1e-3 ? 0.4 : 1 }}>+</button>
    </div>
  )
}
